// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledTypography } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { myAccountOverlayStyles } from "@sc/App/Components/MyAccountOverlay/MyAccountOverlay";
import Edit from "@sc/XDL/Icons/Edit";
import LogOut from "@sc/XDL/Icons/LogOut";
import { fontConfig } from "@sc/XDL/PreStyle/typography";
import { color, createLinkIconStyles } from "@sc/XDL/Utilities";
import { css } from "styled-components";

const sharedSectionTitleStyle: TypographyPresentationProps = {
    variant: "h6",
    forwardAs: "p",
    css: css`
        margin-bottom: 0;
    `,
};

myAccountOverlayStyles.titleWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 78px;
        padding: 12px 0 8px;
        margin: 0 12px 8px;
        border-bottom: 1px solid ${color("common.border")};
    `,
};

myAccountOverlayStyles.title = {
    variant: "h6",
    forwardAs: "p",
    weight: "normal",
    css: css`
        margin-bottom: 0;
    `,
};

myAccountOverlayStyles.personalInfo = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 24px;
            padding-left: 12px;
            padding-right: 12px;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            column-gap: 24px;
        `,
    },
    infoColumnContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
        `,
    },
    title: {
        ...sharedSectionTitleStyle,
    },
    subTitle: {
        size: 12,
        lineHeight: "14px",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 8px;
        `,
    },
    name: {
        size: 12,
        lineHeight: "14px",
        color: "text.main",
        css: css`
            margin-bottom: 4px;
        `,
    },
    label: {
        size: 14,
        color: "text.main",
        lineHeight: "14px",
    },
    accountLink: {
        color: "secondary.main",
        css: css`
            &:not(:last-child) {
                margin-bottom: 4px;
            }
        `,
    },
};

myAccountOverlayStyles.linkItems = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 24px;
            padding-left: 12px;
            padding-right: 12px;
        `,
    },
    title: {
        ...sharedSectionTitleStyle,
    },
    linksContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            column-gap: 24px;
            row-gap: 12px;
            flex-wrap: wrap;
        `,
    },
    linkWrapper: {
        css: css`
            width: calc(50% - 12px);
        `,
    },
    link: {
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            color: "secondary.main",
            css: css`
                line-height: 14px;
                &&& {
                    color: ${color("secondary.main")};
                }
            `,
        },
    },
};

myAccountOverlayStyles.actionSection = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 12px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    changeShipTo: {
        ...createLinkIconStyles(Edit, {
            icon: {
                iconProps: {
                    size: 13,
                    css: css`
                        &,
                        svg {
                            width: 12px;
                        }
                    `,
                },
                position: "right",
            },
            typographyProps: {
                color: "common.ui7",
                weight: fontConfig.weight.regular,
                css: css`
                    &&&${StyledTypography} {
                        color: ${color("common.ui7")};
                    }
                `,
            },
        }),
    },
    signOutButton: {
        ...createLinkIconStyles(LogOut, {
            icon: {
                iconProps: {
                    size: 13,
                    css: css`
                        &,
                        svg {
                            width: 12px;
                        }
                    `,
                },
            },
            css: css`
                margin-left: auto;
            `,
        }),
    },
};
